import React, { forwardRef } from 'react';
import type { Ref } from 'react';
import type { ClassNameProp, VoidCallback } from '../common-types';
import { Direction } from '../common-types';
import type { CommonInputProps } from './CommonInputProps';
import type { ValidIconName } from '../basic';
import { Icon } from '../basic';
import { isEmptyString } from '../../util';
import { useRadioGroupContext } from './RadioGroup';

/**
 * Props for a {@link Radio} component.
 *
 */
export interface RadioProps<T>
  extends ClassNameProp,
    Pick<CommonInputProps<T>, 'value' | 'disabled' | 'readOnly'> {
  /**
   * Placement of the radio label.
   */
  labelPlacement?: Direction;
  /**
   * Change callback.
   */
  onChange?: VoidCallback;
  /**
   * Label of the Radio.
   * The label can be any arbitrary React node.
   */
  label: React.ReactNode;
  /**
   * Custom icon for checked value.
   */
  checkedIcon?: ValidIconName;
  /**
   * Custom icon for unchecked value.
   */
  uncheckedIcon?: ValidIconName;
}

/**
 * Simple Radio input component.
 * The group that the Radio is a part of is controlled through the `name` property.
 * This component only works as a controlled component due to some limitations with type "radio" input elements.
 *
 * @typeParam T type of value used to check if the Radio is checked. When NOT using a RadioGroup, the type has
 * to be either `boolean` or `number`.
 * @param properties a {@link RadioProps} object
 * @return the created {@link JSX.Element}.
 */
export const Radio = forwardRef(function Radio<T>(
  {
    className = '',
    labelPlacement = Direction.RIGHT,
    uncheckedIcon = 'materialicon-radio_button_unchecked',
    checkedIcon = 'materialicon-radio_button_checked',
    value,
    onChange,
    disabled,
    readOnly,
    label,
    ...props
  }: Readonly<RadioProps<T>>,
  ref: Ref<HTMLInputElement>
): JSX.Element {
  const groupContext = useRadioGroupContext<T>();
  let checked;
  if (groupContext) {
    // use context value
    checked =
      groupContext.valueComparator?.(groupContext.value, value) ??
      groupContext.value === value;
  } else if (typeof value === 'boolean') {
    // simply use the passed boolean
    checked = value;
  } else {
    console.error(
      'The type of the passed value is only usable when surrounded with a RadioGroup. ' +
        'Please ensure that the Radio is either surrounded by a RadioGroup or that the value is a boolean.'
    );
    checked = false;
  }

  const cssClasses = [`ehs-radio label-${labelPlacement}`];
  if (checked) cssClasses.push('checked');
  if (disabled || (readOnly && !checked)) {
    cssClasses.push('disabled');
  }
  if (!isEmptyString(className)) cssClasses.push(className);

  // change event handler which calls both the onChange passed to this component
  // and the groupContext (if it exists)
  const onChangeHandler = () => {
    onChange?.();
    groupContext?.onChange?.(value as T); // cast here because onChange only ever fires if something is selected
  };

  return (
    <label
      {...props}
      onBlur={groupContext?.onBlur}
      onFocus={groupContext?.onFocus}
      className={cssClasses.join(' ')}
      tabIndex={-1}
    >
      <input
        type="radio"
        disabled={disabled || (readOnly && !checked)}
        readOnly={readOnly}
        checked={checked}
        onChange={onChangeHandler}
        aria-checked={checked}
        ref={ref}
        name={groupContext?.name}
      />
      <Icon
        aria-label={undefined}
        className="radio-icon"
        iconName={checked ? checkedIcon : uncheckedIcon}
      />
      <span className="label">{label}</span>
    </label>
  );
});
